import { LoadingContainer } from 'peach/components'
import { useParams } from 'peach/router'

import { Page, PageHeader } from 'core/components'
import DynamicForm from 'core/components/lib/DynamicForm/DynamicForm'
import { useGetCompanyConfig, useSetCompanyConfig } from 'core/types'
import { GetCompanyConfigResponse } from 'core/types/generated/company/operations'
import { companySchema } from 'core/utils/generated/formSchemas'
import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'
import { flattenObject } from 'core/utils/objectUtils'

// Helper function to generate field overrides for script fields
const getScriptFieldOverrides = (data: any) => {
  if (!data) return {}

  try {
    const flatData = flattenObject(data)
    return Object.fromEntries(
      Object.keys(flatData)
        .filter((key) => key.toLowerCase().includes('script'))
        .map((key) => [key, 'Textarea' as const]),
    )
  } catch (error) {
    console.warn('Failed to generate script field overrides:', error)
    return {}
  }
}

// Augment the generated schema to include fields that exist in UI but not in API schema
const augmentSchemaWithUiFavicon = (
  schema: Array<StaticFieldSchema>,
): Array<StaticFieldSchema> => {
  const next: Array<StaticFieldSchema> = schema.map((field) => {
    if (field.key !== 'ui') return field

    const hasFavicon = (field.nested || []).some((f) => f.key === 'favicon')
    if (hasFavicon) return field

    const faviconField: StaticFieldSchema = {
      key: 'favicon',
      type: 'string',
      required: false,
      component: 'TextInput',
      fullPath: 'ui.favicon',
    }

    return {
      ...field,
      nested: [...(field.nested || []), faviconField],
    }
  })

  return next
}

const CompanyConfig = () => {
  const { companyId } = useParams<{ companyId?: string }>()
  const { data: config, isLoading } = useGetCompanyConfig({ companyId })

  const { mutateAsync: updateCompanyConfig } = useSetCompanyConfig({
    companyId,
  })

  const sendUpdateConfig = async (value) => {
    await updateCompanyConfig(value)
  }

  return (
    <LoadingContainer loading={isLoading}>
      <Page variant='full'>
        <PageHeader title='Company Config' />
        <DynamicForm
          arraySchemas={{
            'openHours.businessHours.holidays': [
              {
                key: 'name',
                type: 'string',
                required: false,
                component: 'TextInput',
                fullPath: 'name',
              },
              {
                key: 'date',
                type: 'string',
                required: false,
                component: 'TextInput',
                fullPath: 'date',
              },
              {
                key: 'hours',
                type: 'string',
                required: false,
                component: 'TextInput',
                fullPath: 'hours',
              },
            ],
          }}
          data={config as GetCompanyConfigResponse}
          fieldEditMode='toggle'
          fieldOverrides={{
            'brandAssets.copies.consent': 'Textarea',
            'brandAssets.copies.disclosures': 'Textarea',
            'brandAssets.copies.borrowerLoginConsent': 'Textarea',
            'support.outboundCallRecordingConsentScript': 'Textarea',
            'auth.saml.metadataUrl': 'JsonInput',
            'communication.email_from': 'JsonInput',
            'communication.email_inbound.monitored_emails': 'JsonInput',
            // Auto-override any field with "script" in the name to be a textarea
            ...getScriptFieldOverrides(config),
          }}
          onSubmit={sendUpdateConfig}
          schema={augmentSchemaWithUiFavicon(companySchema)}
          submitText='Save'
        />
      </Page>
    </LoadingContainer>
  )
}

export default CompanyConfig
